import React, { useEffect, useCallback, useState, useContext } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { config } from '../../api/config';
import { LoyaltyProgramContext } from './loyalty.program.context';
import {
  formatDate,
  mapLoyaltyProgramStatusColor,
  mapLoyaltyProgramStatus,
  isFilterActive,
  generateHeader,
  buildTableFilterBody,
} from '../../utils';
import {
  Header,
  HeaderActions,
  Title,
  Button,
  Table,
  TableRow,
  TableCell,
  WithSpinner,
  DateFilter,
  SelectFilter,
  ButtonSimple,
} from '../../components';
import { actions } from '../../state';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const LoyaltyProgram = ({
  dispatch,
  loyaltyProgram: { loyaltyPrograms, total, header, loading },
}) => {
  const {
    validFromLtFilter,
    setValidFromLtFilter,
    validFromGtFilter,
    setValidFromGtFilter,
    validToLtFilter,
    setValidToLtFilter,
    validToGtFilter,
    setValidToGtFilter,
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    sortOrder,
    setSortOrder,
    filterBody,
    setFilterBody,
  } = useContext(LoyaltyProgramContext);
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const fetchLoyaltyPrograms = useCallback(
    data => {
      dispatch(actions.loyaltyProgram.getLoyaltyProgramsAction(data));
    },
    [dispatch]
  );

  const clearLoyaltyPrograms = useCallback(() => {
    dispatch(actions.loyaltyProgram.clearLoyaltyProgramsAction());
  }, [dispatch]);

  useEffect(() => () => clearLoyaltyPrograms(), [clearLoyaltyPrograms]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchLoyaltyPrograms({ offset });
  }, [fetchLoyaltyPrograms, page]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const handleSubmit = submitOptions => {
    const generatedFilter = [];

    if (nameFilter.length) {
      generatedFilter.push({ field: 'name', value: nameFilter });
    }

    if (statusFilter.length) {
      generatedFilter.push({ field: 'status', value: statusFilter });
    }

    if (validFromGtFilter) {
      generatedFilter.push({
        field: 'valid_from',
        expression: 'gte',
        value: validFromGtFilter,
        type: 'timestamp',
      });
    }
    if (validFromLtFilter) {
      generatedFilter.push({
        field: 'valid_from',
        expression: 'lte',
        value: validFromLtFilter,
        type: 'timestamp',
      });
    }

    if (validToGtFilter) {
      generatedFilter.push({
        field: 'valid_to',
        expression: 'gte',
        value: validToGtFilter,
        type: 'timestamp',
      });
    }
    if (validToLtFilter) {
      generatedFilter.push({
        field: 'valid_to',
        expression: 'lte',
        value: validToLtFilter,
        type: 'timestamp',
      });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
      })
    );
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setNameFilter([]);
    setValidFromLtFilter();
    setValidFromGtFilter();
    setValidToLtFilter();
    setValidToGtFilter();

    handleSubmit({ clear: true });
    setPage(1);
  };

  const tableHeader = [
    <SelectFilter
      title="Būsena"
      name="status"
      valueType="number"
      array={generateHeader(header.status, mapLoyaltyProgramStatus)}
      filterState={statusFilter}
      setFilterState={setStatusFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', statusFilter)}
    />,
    <SelectFilter
      title="Pavadinimas"
      name="name"
      valueType="string"
      array={generateHeader(header.name)}
      filterState={nameFilter}
      setFilterState={setNameFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', nameFilter)}
    />,
    'Tikslo sk.',
    <DateFilter
      title="Nuo"
      name="valid_from"
      lt={validFromLtFilter}
      setLt={setValidFromLtFilter}
      gt={validFromGtFilter}
      setGt={setValidFromGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('date', validFromLtFilter, validFromGtFilter)}
    />,
    <DateFilter
      title="Iki"
      name="valid_to"
      lt={validToLtFilter}
      setLt={setValidToLtFilter}
      gt={validToGtFilter}
      setGt={setValidToGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('date', validToLtFilter, validToGtFilter)}
    />,
  ];

  const renderTable = () =>
    loyaltyPrograms.map(
      ({ id, status, name, goal_count, valid_from, valid_to }) => (
        <TableRow url={config.APP_PATHS.editLoyaltyProgram(id)} key={id}>
          <TableCell>
            <span style={{ color: mapLoyaltyProgramStatusColor(status) }}>
              {mapLoyaltyProgramStatus(status)}
            </span>
          </TableCell>
          <TableCell>{name}</TableCell>
          <TableCell>{goal_count}</TableCell>
          <TableCell>{formatDate(valid_from)}</TableCell>
          <TableCell>{formatDate(valid_to)}</TableCell>
        </TableRow>
      )
    );

  return (
    <section>
      <Header>
        <Title total={total}>{config.PAGES.loyaltyProgram}</Title>
        <HeaderActions>
          {filterBody && (
            <ButtonSimple type="filter" onClick={clearFilters}>
              Išvalyti filtrus
            </ButtonSimple>
          )}
          <Button color="primary" url={config.APP_PATHS.addLoyaltyProgram}>
            Sukurti
          </Button>
        </HeaderActions>
      </Header>

      <WithSpinner loading={loading}>
        <Table
          header={tableHeader}
          isEmpty={!loyaltyPrograms.length}
          loading={loading}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          serverPagination={true}
          onPageChange={setPage}
        >
          {renderTable()}
        </Table>
      </WithSpinner>
    </section>
  );
};

const mapStateToProps = state => ({
  loyaltyProgram: state.loyaltyProgram,
});

LoyaltyProgram.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(LoyaltyProgram);
